import { friendlyError } from '../../utils/userMessages';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Rating,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close as CloseRounded, StarsRounded as RateReviewRounded, Star as StarRounded } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useAuth } from '../auth/AuthContext';
import { resolveCompanyId } from '../../utils/companyIdentity';
import { getEntityRatingSummary, getMyEntityRating, submitEntityRating } from '../../services/ratings/entityRatingService';

const CATEGORY_ROWS = [
  ['communication', 'Communication'],
  ['professionalism', 'Professionalism'],
  ['behaviour', 'Behaviour'],
  ['reliability', 'Reliability'],
  ['businessValue', 'Business value'],
];

const defaultValues = { overall: 5, communication: 5, professionalism: 5, behaviour: 5, reliability: 5, businessValue: 5 };

export default function EntityRatingButton({
  targetType,
  targetId,
  targetName,
  target = {},
  compact = false,
  color = '#E9A220',
  onSaved,
}) {
  const { currentUser, userData, companyData } = useAuth();
  const ownCompanyId = resolveCompanyId({ currentUser, userData, companyData });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(defaultValues);
  const [comment, setComment] = useState('');
  const [contextType, setContextType] = useState('business_interaction');
  const [summary, setSummary] = useState(target?.ratingSummary || null);

  const disabledReason = useMemo(() => {
    if (!currentUser?.uid) return 'Sign in to rate';
    if (targetType === 'user' && String(targetId) === String(currentUser.uid)) return 'You cannot rate yourself';
    if (targetType === 'company' && ownCompanyId && String(targetId) === String(ownCompanyId)) return 'You cannot rate your own company';
    return '';
  }, [currentUser?.uid, targetType, targetId, ownCompanyId]);

  const openDialog = async (event) => {
    event?.stopPropagation?.();
    if (disabledReason) return toast.error(disabledReason);
    setOpen(true);
    setLoading(true);
    try {
      const existing = await getMyEntityRating({ raterUserId: currentUser.uid, targetType, targetId });
      if (existing) {
        const next = { ...defaultValues };
        Object.keys(next).forEach((key) => { if (existing[key]) next[key] = Number(existing[key]); });
        setValues(next);
        setComment(existing.comment || '');
        setContextType(existing.contextType || 'business_interaction');
      } else {
        setValues(defaultValues);
        setComment('');
        setContextType('business_interaction');
      }
    } catch (error) {
      console.warn('Existing rating unavailable:', error);
    } finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const nextSummary = await submitEntityRating({
        raterUserId: currentUser.uid,
        raterCompanyId: ownCompanyId || '',
        targetType,
        targetId,
        targetName,
        values,
        comment,
        contextType,
      });
      setSummary(nextSummary);
      onSaved?.(nextSummary);
      toast.success(`Rating saved for ${targetName || targetType}.`);
      setOpen(false);
    } catch (error) {
      console.error('Unable to save rating:', error);
      toast.error(friendlyError(error, 'Unable to save rating.'));
    } finally { setSaving(false); }
  };

  useEffect(() => {
    let active = true;
    if (!targetType || !targetId) return () => { active = false; };
    getEntityRatingSummary(targetType, targetId).then((next) => { if (active && next?.count) setSummary(next); }).catch(() => undefined);
    return () => { active = false; };
  }, [targetType, targetId]);

  const avg = Number(summary?.average || target?.ratingSummary?.average || 0);
  const count = Number(summary?.count || target?.ratingSummary?.count || 0);

  return <>
    {compact ? (
      <Tooltip title={disabledReason || (count ? `${avg.toFixed(1)} from ${count} review${count === 1 ? '' : 's'} · click to rate` : 'Rate experience')}>
        <span>
          <Button
            disabled={Boolean(disabledReason)}
            onClick={openDialog}
            startIcon={<StarRounded sx={{ fontSize: '14px !important' }} />}
            sx={{ minWidth: count ? 61 : 34, height: 29, px: count ? .75 : .55, borderRadius: '9px', color: '#6A4B00 !important', bgcolor: `${color}12 !important`, border: `1px solid ${color}25 !important`, boxShadow: '0 3px 9px rgba(25,57,78,.045)', fontSize: 9.4, fontWeight: 900, '& .MuiButton-startIcon': { mr: count ? .35 : 0 } }}
          >{count ? avg.toFixed(1) : ''}</Button>
        </span>
      </Tooltip>
    ) : (
      <Button
        disabled={Boolean(disabledReason)}
        onClick={openDialog}
        variant="outlined"
        startIcon={<StarRounded />}
        sx={{ minHeight: 36, borderRadius: '10px', textTransform: 'none', fontWeight: 850, color: '#6A4B00 !important', borderColor: `${color}55 !important`, bgcolor: `${color}10 !important` }}
      >{count ? `Rate · ${avg.toFixed(1)} (${count})` : 'Rate Experience'}</Button>
    )}

    <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm" slotProps={{ paper: { className: 'tf-premium-dialog' } }} onClick={(e) => e.stopPropagation()}>
      <DialogTitle sx={{ p: 2, pb: 1.2 }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box><Typography sx={{ fontWeight: 950, color: '#182235' }}>Rate {targetType === 'company' ? 'Company' : 'Professional'}</Typography><Typography sx={{ mt: .2, fontSize: 10.8, color: '#738196' }}>Rate communication, behaviour and your overall business experience with {targetName || 'this profile'}.</Typography></Box>
          <IconButton onClick={() => setOpen(false)} disabled={saving}><CloseRounded /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        {loading ? <Typography sx={{ py: 4, textAlign: 'center', color: '#718096' }}>Loading your previous rating…</Typography> : <Stack spacing={1.4}>
          <Box sx={{ p: 1.35, borderRadius: '14px', bgcolor: 'rgba(233,162,32,.07)', border: '1px solid rgba(233,162,32,.15)' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
              <Box><Typography sx={{ fontSize: 11, fontWeight: 900, color: '#6A4B00' }}>Overall experience</Typography><Typography sx={{ fontSize: 9.6, color: '#8A7441' }}>Your overall impression of the interaction.</Typography></Box>
              <Rating value={values.overall} onChange={(_, value) => value && setValues((prev) => ({ ...prev, overall: value }))} precision={1} size="large" />
            </Stack>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
            {CATEGORY_ROWS.map(([key, label]) => <Box key={key} sx={{ p: 1, borderRadius: '12px', border: '1px solid rgba(31,62,82,.08)', bgcolor: '#fff' }}><Typography sx={{ fontSize: 10.3, fontWeight: 850, color: '#34445A' }}>{label}</Typography><Rating value={values[key]} onChange={(_, value) => value && setValues((prev) => ({ ...prev, [key]: value }))} size="small" /></Box>)}
          </Box>
          <TextField select size="small" label="What are you rating?" value={contextType} onChange={(e) => setContextType(e.target.value)}>
            <MenuItem value="business_interaction">Business interaction / conversation</MenuItem>
            <MenuItem value="meeting">Meeting experience</MenuItem>
            <MenuItem value="service">Service / commercial experience</MenuItem>
            <MenuItem value="professional_behaviour">Professional behaviour</MenuItem>
          </TextField>
          <TextField multiline minRows={3} label="Optional review" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share useful, professional feedback about the interaction…" />
          <Stack direction="row" spacing={.7} sx={{ alignItems: 'center' }}><RateReviewRounded sx={{ fontSize: 16, color: '#7A8A9B' }} /><Typography sx={{ fontSize: 9.8, color: '#7A8A9B' }}>Keep reviews factual and focused on business conduct. You can update your rating later.</Typography></Stack>
        </Stack>}
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}><Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={save} disabled={saving || loading} sx={{ color: '#fff !important', fontWeight: 900 }}>{saving ? 'Saving…' : 'Save Rating'}</Button></DialogActions>
    </Dialog>
  </>;
}
